import cn from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';

import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import { ComponentType } from 'shared/entities/components/Component';
import {
  ButtonProps,
  ButtonSize,
  ButtonColor,
  ButtonStyle,
  mapButtonSizeToIconSize,
  mapButtonSizeToLoaderSpinnerSize,
  buttonFontProps,
  mapButtonColorToLoaderColor,
  ButtonContentPosition
} from 'shared/newEntities/components/Button';
import Typography from 'shared/newComponents/Typography';
import { TypographyWeight } from 'shared/newEntities/components/Typography';
import Reference from 'shared/newComponents/Reference';
import Tag from 'shared/newComponents/Tag';

import styles from './Button.modules.scss';

const Button = React.forwardRef<any | null, ButtonProps>((props, ref) => {
  const {
    className,
    ariaLabel,
    children,
    disabled,
    isLoading,
    fullWidth = false,
    size = ButtonSize.md,
    color = ButtonColor.blue,
    style = ButtonStyle.common,
    Icon,
    RightIcon,
    transparentBorder = false,
    withoutPadding = false,
    contentPosition = ButtonContentPosition.center,
    withoutBorder = false,
    iconStyle,
    fontWeight,
    fixHeight,
    showLeftIcon = true,
    reference,
    tag
  }: ButtonProps = props;

  //показываем иконку слева, если не загрузка
  //если загрузка, то только когда есть текст у кнопки
  const showLeftIconComp =
    showLeftIcon && Icon && ((isLoading && !!children) || !isLoading);

  //показываем иконку справа, если не загрузка
  //если загрузка, то только когда есть текст у кнопки
  const showRightIcon = RightIcon && ((isLoading && !!children) || !isLoading);

  const iconSize = mapButtonSizeToIconSize[size];
  const fontProps = {
    fontWeight:
      fontWeight ??
      (size === ButtonSize.xxl
        ? TypographyWeight.bold
        : TypographyWeight.semiBold),
    ...buttonFontProps[size]
  };
  const loaderSize = mapButtonSizeToLoaderSpinnerSize[size];

  const content = (
    <>
      {tag && <Tag styleName="button__tag" {...tag} />}
      {showLeftIconComp && (
        <div
          styleName={cn(
            'button__icon',
            !children && 'button__icon_without-content'
          )}
        >
          <Icon iconSize={iconSize} iconStyle={iconStyle} />
        </div>
      )}
      {children && (
        <Typography
          {...fontProps}
          fontColor={null}
          styleName={cn(fixHeight && 'button_fix-height')}
        >
          {children}
        </Typography>
      )}
      {isLoading && (
        <LoaderSpinner
          styleName="button__loader"
          color={mapButtonColorToLoaderColor[color]}
          size={loaderSize}
        />
      )}
      {showRightIcon && (
        <div styleName="button__icon button__icon_right">
          <RightIcon iconSize={iconSize} />
        </div>
      )}
    </>
  );

  const styleName = cn(
    'button',
    `button_${size}`,
    `button_content_${contentPosition}`,
    `button_content_${contentPosition}`,
    style === ButtonStyle.common && 'button_common',
    (fullWidth || (!!reference && !!children)) && 'button_full',
    transparentBorder && 'button_transparent-border',
    !children && !withoutPadding && 'button_without-content',
    withoutPadding && 'button_without-padding',
    withoutBorder && 'button_without-border'
  );

  const actualClassName = cn(
    style === ButtonStyle.transparent && styles[`button_transparent-${color}`],
    style === ButtonStyle.common && styles[`button_${color}`],
    !reference && className
  );

  const commonProps = {
    className: actualClassName,
    'aria-label': ariaLabel,
    disabled
  };

  let button;

  switch (props.type) {
    case ComponentType.link:
      button = (
        <Link
          styleName={styleName}
          {...commonProps}
          to={props.to}
          ref={ref}
          onClick={props.onClick}
          target={props.target}
        >
          {content}
        </Link>
      );
      break;
    case ComponentType.a: {
      const extraProps =
        props.target === '_blank'
          ? {
              rel: 'noopener noreferrer'
            }
          : {};
      button = (
        <a
          styleName={styleName}
          {...commonProps}
          href={props.href}
          target={props.target}
          {...extraProps}
          ref={ref}
          onClick={props.onClick}
        >
          {content}
        </a>
      );
      break;
    }
    default:
      button = (
        <button
          styleName={styleName}
          {...commonProps}
          type="button"
          onClick={props.onClick}
          ref={ref}
        >
          {content}
        </button>
      );
  }

  if (!reference) {
    return button;
  }

  return (
    <Reference
      className={className}
      trigger={<div styleName="button__reference">{button}</div>}
      {...reference}
    />
  );
});

export default Button;
